import { JSX, useCallback, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import * as Dfns from 'date-fns/fp';
import { useQueryClient } from '@tanstack/react-query';
import {
  MdSend,
  MdCheck,
  MdCheckCircle,
  MdCancel,
  MdLoop,
  MdRefresh,
  MdHistory
} from 'react-icons/md';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { Form } from 'react-bootstrap';
import { Checkbox } from '@Components/FormCheck';
import {
  TYPES_TO_NAMES,
  OrderStatus,
  ORDER_STATUS_TO_NAMES
} from '@blisspointmedia/bpm-types/dist/LinearBuying';
import { CURRENT_WEEK_START } from '@src/utils/date-utils';
import { LinearBuyingLambdaFetch, pollS3, S3Put } from '../../utils/fetch-utils';
import { Mixpanel, MxE } from '../../utils/mixpanelWrapper';
import { useSetAreYouSure, useSetError } from '../../redux/modals';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { useExperimentFlag } from '../../utils/experiment-context';
import { Page } from '../../Components/Page';
import { PAGE_NAMES } from '../../Components/Page/page-constants';
import { Spinner } from '../../Components/Spinner';
import { BPMTable, CellRenderer, Header, TypedHeader } from '../../Components/StickyTable';
import { Button } from '../../Components/Button';
import { IconButton } from '../../Components/IconButton';
import { Dropdown, DropdownToggleType } from '../../Components/Dropdown';
import { MenuItem } from '../../Components/Dropdown/dropdown-constants';
import { DatePicker } from '../../Components/DatePicker';
import { Appearance, Intent, Placement, Size } from '../../Components/designConstants';
import { TableLoading } from '../../Components/TableEmptyState';
import { TablePage } from '../../Components/TablePage';
import {
  FileInfo,
  FormattedOrderRow,
  MONTH_DAY_DATE_FORMAT,
  MONTH_DAY_TIME_DATE_FORMAT,
  OrderAction,
  OrderActionType,
  OrderNotesMap,
  S3_LINEAR_ORDERS,
  SelectedOrdersMap
} from './linear-orders-constants';
import { OrderHistoryModal } from './OrderHistoryModal';
import { linearOrdersQueryKey, useLinearOrders } from './use-linear-orders';
import './LinearOrders.scss';

const PDF_STATUS_MAP: Record<string, JSX.Element> = {
  pending: <MdLoop size={25} />,
  success: <MdCheckCircle size={25} />,
  failed: <MdCancel size={25} />
};

const ORDER_ACTION_OPTIONS: MenuItem<OrderActionType>[] = [
  { value: OrderAction.SEND, label: 'Send Selected', leadingContent: <MdSend /> },
  { value: OrderAction.MARK_SENT, label: 'Mark Selected as Sent', leadingContent: <MdCheck /> },
  { value: OrderAction.REGENERATE, label: 'Regenerate Selected', leadingContent: <MdRefresh /> },
  { value: OrderAction.CANCEL, label: 'Cancel Selected', leadingContent: <MdCancel /> }
];

/**
 * Determines if an order should be allowed to be selected to be sent.
 */
const shouldAllowSending = (status: OrderStatus) =>
  status === OrderStatus.PENDING || status === OrderStatus.CANCELED;

export const LinearOrders = (): JSX.Element => {
  const companyInfo = useCompanyInfo();
  const setError = useSetError();
  const setAreYouSure = useSetAreYouSure();
  const queryClient = useQueryClient();
  const company = companyInfo.cid;
  const enableCampaigns = useExperimentFlag('enableLinearCampaigns');
  const enableRadioBuying = useExperimentFlag('enableRadioBuying');

  const [week, setWeek] = useState(CURRENT_WEEK_START);
  const { data: orders, isLoading, error } = useLinearOrders(company, week);
  const [downloading, setDownloading] = useState<Record<string, boolean>>({});

  const [orderNotes, setOrderNotes] = useState<OrderNotesMap>({});
  const [saving, setSaving] = useState(false);

  const [selectedOrders, setSelectedOrders] = useState<SelectedOrdersMap>({});
  const [selectAll, setSelectAll] = useState(false);
  const [sending, setSending] = useState(false);

  const [fileData, setFileData] = useState<Record<string, FileInfo>>({});

  const [orderModalData, setOrderModalData] = useState<FormattedOrderRow>();

  useEffect(() => {
    if (error) {
      setError({ message: error.message, reportError: error });
    }
  }, [error, setError]);

  // After a mutating action (send/regenerate/cancel/save), refetch this exact
  // company+week's orders instead of the old setOrders(undefined) + always-mounted-table
  // reset -- the query key already scopes to company+week, so no manual reset is needed
  // on a week change (DatePicker's onChange below): changing `week` alone triggers React
  // Query to load (or serve cached) rows for the new key.
  const invalidateOrders = useCallback(
    () => queryClient.invalidateQueries({ queryKey: linearOrdersQueryKey(company, week) }),
    [queryClient, company, week]
  );

  const downloadPDF = useCallback(
    async (id: number, path: string, isV1?: boolean) => {
      setDownloading(current => ({ ...current, [id]: true }));
      const bucket = isV1 ? 'bpm-orders' : 'bpm-linear-orders';
      try {
        await pollS3({
          bucket,
          mimeType: 'application/pdf',
          filename: path,
          autoDownload: true,
          timeout: 10
        });
        setDownloading(current => ({ ...current, [id]: false }));
      } catch (e) {
        setDownloading(current => ({ ...current, [id]: false }));
        setError({
          message: `Failed to download PDF ${id}. Error: ${e.message}`,
          reportError: e
        });
      }
    },
    [setError]
  );

  // Upload confirmation to S3.
  const uploadConfirmationToS3 = (key: string, file: File) => {
    const fileReader = new FileReader();

    fileReader.onload = event => {
      const data = event.target?.result;
      if (typeof data !== 'string') {
        return;
      }

      S3Put(S3_LINEAR_ORDERS, key, data, {
        bufferEncoding: 'binary',
        contentType: file.type
      });
    };

    fileReader.readAsBinaryString(file);
  };

  // Upload all order confirmations to S3, and then confirm them in the DB.
  const uploadAllConfirmations = async () => {
    // Upload each file to S3
    try {
      for (const id of Object.keys(fileData)) {
        const { s3Key, file } = fileData[id];
        if (file) {
          uploadConfirmationToS3(s3Key, file);
        }
      }

      // Confirm orders in DB
      await LinearBuyingLambdaFetch('/markOrdersAsConfirmed', {
        method: 'POST',
        body: { orders: Object.keys(fileData) }
      });
    } catch (e) {
      setError({
        message: `Failed to upload confirmations. Error: ${e.message}`,
        reportError: e
      });
    }
  };

  // Add formatted values of Order, Status, and Type columns so that the values are more
  // clear in the filter bar.
  const formattedRows: FormattedOrderRow[] = useMemo(
    () =>
      (orders ?? []).map(orderRow => {
        const { id, status, type, revision, start_week, end_week } = orderRow;
        const start = Dfns.format(MONTH_DAY_DATE_FORMAT)(Dfns.parseISO(start_week));
        // An open-ended blanket instruction has no end week, so there is nothing to format.
        const end = end_week
          ? Dfns.format(MONTH_DAY_DATE_FORMAT)(Dfns.parseISO(end_week))
          : 'ongoing';
        const formattedWeeks = `${start} - ${end}`;
        const formattedOrder = `${id}-${revision}`;
        const formattedOrderStatus = ORDER_STATUS_TO_NAMES[`${status}`];
        const formattedOrderType = TYPES_TO_NAMES[`${type}`];

        return {
          ...orderRow,
          formattedWeeks,
          formattedOrder,
          formattedOrderStatus,
          formattedOrderType
        };
      }),
    [orders]
  );

  // Loop through this when "select all" checkbox is clicked. We only look at rows that are currently being filtered.
  const [filteredData, setFilteredData] = useState(formattedRows);

  const allHeaders: TypedHeader<FormattedOrderRow>[] = [
    {
      label: '',
      name: 'id',
      width: 63,
      nonInteractive: true,
      renderer: row => {
        const { id, is_traffic, is_blanket } = row;
        const idString = id.toString();
        return (
          <Checkbox
            checked={Boolean(selectedOrders[idString])}
            onChange={() => {
              if (selectedOrders[idString]) {
                setSelectedOrders(current => {
                  const next = { ...current };
                  delete next[idString];
                  return next;
                });
              } else {
                setSelectedOrders(current => ({
                  ...current,
                  [idString]: { id, isTraffic: is_traffic, isBlanket: is_blanket }
                }));
              }
            }}
          />
        );
      }
    },
    {
      label: 'Weeks',
      name: 'formattedWeeks',
      renderer: row => row.formattedWeeks
    },
    {
      label: 'Order',
      name: 'formattedOrder',
      width: 140,
      renderer: row => {
        const { id, s3Path, formattedOrder, isV1, revision } = row;
        if (downloading[id]) {
          return <Spinner />;
        }
        return (
          <>
            <Button
              size={Size.LG}
              appearance={Appearance.EMPTY}
              className='pdf-link'
              onClick={() => downloadPDF(id, s3Path, isV1)}
            >
              {formattedOrder}
            </Button>
            {(revision > 1 || (!row.is_traffic && row.status === OrderStatus.CONFIRMED)) && (
              <IconButton
                appearance={Appearance.OUTLINED}
                size={Size.SM}
                onClick={() => {
                  setOrderModalData(row);
                  Mixpanel.track(MxE.ModalOpened, {
                    page: PAGE_NAMES.LINEAR_ORDERS,
                    modal: 'order_history'
                  });
                }}
              >
                <MdHistory />
              </IconButton>
            )}
          </>
        );
      }
    },
    { label: 'Network', name: 'network', flex: 1 },
    { label: 'Avail', name: 'avail', width: 85 },
    {
      label: 'Version',
      name: 'orderVersion',
      flex: 1,
      renderer: row => row.orderVersion
    },
    {
      label: 'Type',
      name: 'formattedOrderType',
      width: 100,
      // Blanket instructions share this table with per-line traffic, and the two are acted on
      // differently -- the type column is where that difference has to be visible.
      renderer: row =>
        row.is_blanket ? `${row.formattedOrderType} (Blanket)` : row.formattedOrderType
    },
    {
      label: 'Campaign',
      name: 'campaign',
      flex: 1,
      renderer: row => row.campaign
    },
    {
      label: 'Order Notes',
      name: 'notes',
      flex: 1,
      renderer: row => (
        <Form.Control
          className='notes'
          disabled={row.end_week < CURRENT_WEEK_START}
          as='textarea'
          maxLength={450}
          value={orderNotes[row.id]?.notes ?? (row.notes || '')}
          onChange={e => {
            const val = e.target.value;
            const isTraffic = row.is_traffic;
            const isBlanket = row.is_blanket;
            setOrderNotes(current => ({
              ...current,
              [row.id]: { notes: val, isTraffic, isBlanket }
            }));
          }}
        />
      )
    },
    {
      label: 'Status',
      name: 'formattedOrderStatus',
      flex: 1,
      renderer: row => {
        const { id, s3Path, formattedOrderStatus } = row;
        return (
          <div className={cn('order-status', formattedOrderStatus.toLowerCase())}>
            {formattedOrderStatus}
            {row.status === OrderStatus.SENT && (
              <input
                className='file-input'
                type='file'
                onChange={e => {
                  const file = e.target.files?.[0];
                  const approvedPath = s3Path.replace('.pdf', '_Approved.pdf');
                  setFileData(current => ({ ...current, [id]: { s3Key: approvedPath, file } }));
                }}
              />
            )}
          </div>
        );
      }
    },
    {
      label: 'Last Modified',
      name: 'lastmodified',
      width: 150,
      renderer: row => {
        const { lastmodified, lastuser } = row;
        const formattedDate = Dfns.format(MONTH_DAY_TIME_DATE_FORMAT)(Dfns.parseISO(lastmodified));
        const name = lastuser && lastuser.split('@')[0];
        return <div>{`${name} at ${formattedDate}`}</div>;
      }
    },
    {
      label: 'PDF Status',
      name: 'pdf_status',
      width: 90,
      renderer: row => (
        <div className={cn('pdf-status', row.pdf_status)}>{PDF_STATUS_MAP[row.pdf_status]}</div>
      )
    }
  ];

  const tableHeaders = enableCampaigns
    ? allHeaders
    : allHeaders.filter(header => header.name !== 'campaign');

  const headersRenderer: CellRenderer<Header> = useCallback(
    ({ data, columnIndex }) => {
      if (columnIndex === 0) {
        return (
          <Checkbox
            checked={selectAll}
            onChange={() => {
              if (selectAll) {
                setSelectedOrders({});
              } else {
                const selectAllMap: SelectedOrdersMap = {};
                for (const { id, is_traffic, is_blanket } of filteredData) {
                  selectAllMap[id.toString()] = {
                    id,
                    isTraffic: is_traffic,
                    isBlanket: is_blanket
                  };
                }
                setSelectedOrders(current => ({ ...current, ...selectAllMap }));
              }

              setSelectAll(!selectAll);
            }}
          />
        );
      }
      return <>{data}</>;
    },
    [filteredData, selectAll]
  );

  const sendSelected = async (sendEmail: boolean) => {
    let allowSendingAll = true;
    for (const row of formattedRows) {
      const { id, status } = row;
      const allowSending = shouldAllowSending(status);
      if (!allowSending && selectedOrders[id]) {
        allowSendingAll = false;
        break;
      }
    }

    try {
      if (sendEmail && !allowSendingAll) {
        await setAreYouSure({
          title: 'Resend orders to networks?',
          message:
            "You have orders selected that have already been sent. This would resend them. You probably don't want to do this. Do you wish to continue?",
          cancelText: 'Never mind',
          okayText: 'Okay',
          intent: Intent.ERROR
        });
      }
    } catch {
      return;
    }

    try {
      if (sendEmail) {
        await setAreYouSure({
          title: 'Send orders to networks?',
          message:
            'You are about to send the selected order PDFs to their associated networks.  Do you wish to continue?',
          cancelText: 'Never mind',
          okayText: 'Okay'
        });
      }
    } catch {
      return;
    }

    try {
      setSending(true);
      await LinearBuyingLambdaFetch('/sendOrdersAndTraffic', {
        method: 'POST',
        body: { company, orders: Object.values(selectedOrders), sendEmail }
      });
      Mixpanel.track(MxE.ActionTriggered, {
        page: PAGE_NAMES.LINEAR_ORDERS,
        action: sendEmail ? 'send' : 'mark_as_sent'
      });
      invalidateOrders();
      setSelectedOrders({});
      setSelectAll(false);
      setSending(false);
    } catch (e) {
      setSending(false);
      setError({ message: e.message, reportError: e });
    }
  };

  const regenerateSelected = useCallback(async () => {
    try {
      await setAreYouSure({
        title: 'Regenerate PDFs?',
        message: 'You are about to regenerate the selected order PDFs.  Do you wish to continue?',
        cancelText: 'Never mind',
        okayText: 'Okay'
      });
    } catch {
      return;
    }

    try {
      setSending(true);
      await LinearBuyingLambdaFetch('/regeneratePDFs', {
        method: 'POST',
        body: { orders: Object.values(selectedOrders), useExperiment: Boolean(enableRadioBuying) }
      });
      Mixpanel.track(MxE.ActionTriggered, {
        page: PAGE_NAMES.LINEAR_ORDERS,
        action: OrderAction.REGENERATE
      });
      invalidateOrders();
      setSelectedOrders({});
      setSelectAll(false);
      setSending(false);
    } catch (e) {
      setSending(false);
      setError({ message: e.message, reportError: e });
    }
  }, [enableRadioBuying, selectedOrders, setAreYouSure, setError, invalidateOrders]);

  const cancelSelected = useCallback(async () => {
    try {
      await setAreYouSure({
        title: 'Cancel Orders?',
        message: `You are about to cancel the selected orders. This will zero-out rows for future weeks, but leave current week rows alone.
        If this order is pending and has never been sent to a network, it will just be deleted.
        Do you wish to continue?`,
        cancelText: 'Never mind',
        okayText: 'Okay'
      });
    } catch {
      return;
    }

    try {
      setSending(true);
      await LinearBuyingLambdaFetch('/cancelOrders', {
        method: 'POST',
        body: { orders: Object.values(selectedOrders), useExperiment: Boolean(enableRadioBuying) }
      });
      Mixpanel.track(MxE.ActionTriggered, {
        page: PAGE_NAMES.LINEAR_ORDERS,
        action: OrderAction.CANCEL
      });
      invalidateOrders();
      setSelectedOrders({});
      setSelectAll(false);
      setSending(false);
    } catch (e) {
      setSending(false);
      setError({ message: e.message, reportError: e });
    }
  }, [enableRadioBuying, selectedOrders, setAreYouSure, setError, invalidateOrders]);

  const saveChanges = async () => {
    try {
      setSaving(true);
      if (Object.keys(orderNotes).length > 0) {
        const processedOrderNotes = Object.keys(orderNotes).map(id => {
          const { notes, isTraffic, isBlanket } = orderNotes[id];
          return { id: parseInt(id, 10), notes, isTraffic, isBlanket };
        });

        await LinearBuyingLambdaFetch('/saveOrderNotes', {
          method: 'POST',
          body: { orders: processedOrderNotes, useExperiment: Boolean(enableRadioBuying) }
        });
        Mixpanel.track(MxE.RecordSaved, { page: PAGE_NAMES.LINEAR_ORDERS, entity: 'order_notes' });
      }

      if (Object.keys(fileData).length > 0) {
        await uploadAllConfirmations();
        Mixpanel.track(MxE.RecordCreated, {
          page: PAGE_NAMES.LINEAR_ORDERS,
          entity: 'order_confirmation'
        });
      }
      invalidateOrders();
      setOrderNotes({});
      setFileData({});
      setSelectedOrders({});
      setSelectAll(false);
      setSaving(false);
    } catch (e) {
      setSaving(false);
      setError({ message: e.message, reportError: e });
    }
  };

  const noneSelected = Object.keys(selectedOrders).length === 0;
  const hasPendingChanges = Object.keys(orderNotes).length > 0 || Object.keys(fileData).length > 0;

  const onSelectAction = (action: OrderActionType) => {
    switch (action) {
      case OrderAction.SEND:
        sendSelected(true);
        break;
      case OrderAction.MARK_SENT:
        sendSelected(false);
        break;
      case OrderAction.REGENERATE:
        regenerateSelected();
        break;
      case OrderAction.CANCEL:
        cancelSelected();
        break;
      default:
        break;
    }
  };

  const renderTableLoading = useCallback(
    () => (
      <TableLoading
        isLoading={isLoading}
        error={error}
        emptyMessage='No orders for the selected week.'
      />
    ),
    [isLoading, error]
  );

  return (
    <Page
      title={PAGE_NAMES.LINEAR_ORDERS}
      pageType={PAGE_NAMES.LINEAR_ORDERS}
      isLoading={isLoading}
      actions={
        <div className='linear-orders-actions'>
          {hasPendingChanges && (
            <>
              <Button
                size={Size.SM}
                design={Intent.ERROR}
                onClick={() => {
                  setOrderNotes({});
                  setFileData({});
                }}
              >
                Clear Changes
              </Button>
              <Button size={Size.SM} design={Intent.SUCCESS} loading={saving} onClick={saveChanges}>
                Save Changes
              </Button>
            </>
          )}
          {sending && <Spinner />}
          <Dropdown<OrderActionType>
            type={DropdownToggleType.ICON_ONLY}
            size={Size.LG}
            overlayPlacement={Placement.RIGHT}
            className='options-dropdown'
            leadingIcon={<BsThreeDotsVertical color='white' />}
            placeholder=''
            disabled={noneSelected}
            options={ORDER_ACTION_OPTIONS}
            onChange={onSelectAction}
          />
          <DatePicker
            isSingle
            range={{ start: week, end: week }}
            isOutsideRange={() => false}
            isDayBlocked={date => Dfns.getDay(Dfns.parseISO(date)) !== 1}
            onChange={({ start }) => {
              if (!start) {
                return;
              }
              setWeek(start);
              setSelectAll(false);
              setSelectedOrders({});
            }}
          />
        </div>
      }
    >
      <TablePage tableClassName='linear-orders-table'>
        <BPMTable
          data={formattedRows}
          headers={tableHeaders}
          headersRenderer={headersRenderer}
          onFilteredDataChange={setFilteredData}
          noRowsRenderer={renderTableLoading}
        />
      </TablePage>
      {orderModalData && (
        <OrderHistoryModal
          orderInfo={orderModalData}
          downloadPDF={downloadPDF}
          onClose={() => setOrderModalData(undefined)}
        />
      )}
    </Page>
  );
};
